import { useState, useMemo, useCallback } from 'react'
import type { JSX } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'
import * as m from 'motion/react-m'

import { clsx } from 'clsx'

import {
  Flex,
  PageHeader,
  PageLayout,
  Tabs,
  pageHeaderStyles,
} from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { ChannelNeedsRefresh } from '~publish/pages/Community/components/ChannelRefresh/ChannelNeedsRefresh'
import { ChannelRefreshUnsuccessful } from '~publish/pages/Community/components/ChannelRefresh/ChannelRefreshUnsuccessful'
import { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'
import { FeedbackWidget } from '~publish/components/FeedbackWidget/FeedbackWidget'
import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import { useCommunityCountsContextOptional } from '~publish/contexts/CommunityCountsContext'
import { canPerform } from '~publish/utils/permissions/canPerform'
import { selectEnabledChannelIds } from '~publish/utils/permissions/effectiveChannelSelection'

import { ChannelRefreshNotice } from './ChannelRefreshNotice'
import { ChannelRefreshUnsuccessfulNotice } from './ChannelRefreshUnsuccessfulNotice'
import { NoSupportedChannelsEmptyState } from './NoSupportedChannelsEmptyState'
import { BackfillLoadingBanner } from '../Comments/CommentsList/BackfillLoadingBanner'
import { useActiveBackfillChannel } from '../hooks/backfill/useActiveBackfillChannel'
import { CommentsByPostView } from '../Comments/CommentsByPost'
import { DMsView } from '../DMs'
import { CommunityFilters } from '../CommunityFilters'
import { PersistedStatusSync } from '../CommunityFilters/PersistedStatusSync'
import { useFilters } from '../CommunityFilters/useFilters'
import { CommentsList } from '../Comments/CommentsList'
import { CommentsOrPostsViewToggle } from '../Comments/CommentsOrPostsViewToggle'
import { useCommunityConfig } from '../CommunityConfig'
import { CommunityTabBar } from '../components/CommunityTabBar'
import { NarrowHeaderSlot } from '../components/NarrowHeaderSlot'
import { CommunityPageTitle } from '../CommunityPageTitle'
import { MentionsCounterPromoBanner } from '../components/MentionsCounterPromoBanner'
import { useMentionsNeedsRefresh } from '../hooks/mentions/useMentionsNeedsRefresh'
import { useAvailableTabs } from '../hooks/navigation/useAvailableTabs'
import { useFilterHandlers } from '../hooks/navigation/useFilterHandlers'
import { useTabNavigation } from '../hooks/navigation/useTabNavigation'
import { useCommentsTracking } from '../hooks/tracking/useCommentsTracking'
import { useBannerToCoachMarkFlow } from '../hooks/ui/useBannerToCoachMarkFlow'
import { usePersistedViewPreference } from '../hooks/usePersistedViewPreference'
import { MentionsInstagramLoginNotice, MentionsList } from '../Mentions'
import { NewCommentsAvailableButton } from '../NewCommentsAvailableButton'
import { NewMentionsAvailableButton } from '../NewMentionsAvailableButton'
import { ViewOnlyHintButton } from '../shared/ViewOnlyHintButton'
import { isViewOnlyChannel } from '../shared/viewOnlyScenario'
import { AccessRestricted } from '../SingleChannelView/AccessRestricted'
import { DEFAULT_TAB, TAB_LABELS } from '../types'
import type { CommunityTab } from '../types'
import { isGoogleBusinessChannel } from '../util/channelHelpers'
import { isValidCommunityTab } from '../util/typeGuards'

import { AllChannelsSyncActions } from './AllChannelsSyncActions'
import styles from './AllChannelsView.module.css'

const PageLayoutMotion = m.create(PageLayout)

const NO_COMMUNITY_ACCESS_CHANNEL_TOOLTIP =
  "You don't have Community access to this channel."

const NO_COMMENTS_SUPPORT_CHANNEL_TOOLTIP =
  "Community isn't available for this channel yet."

const NO_MENTIONS_SUPPORT_CHANNEL_TOOLTIP =
  "Mentions aren't available for this channel yet."

type CommentsChannel = NonNullable<
  ReturnType<typeof useCommentsChannels>['channels']
>[number]

type AllChannelsData = {
  channelsSupportedInComments: CommentsChannel[]
  channelsForFilter: CommentsChannel[]
  disabledChannelIds: Set<string>
  getDisabledChannelReason: (channelId: string) => React.ReactNode
  viewOnlyChannelIds: Set<string>
  manageableChannelIds: string[]
  viewableChannels: CommentsChannel[]
  filteredChannels: CommentsChannel[]
  /**
   * Whether the channel selector narrows the view. False means `filteredChannels`
   * is every viewable channel, not a chosen subset.
   */
  isChannelFilterActive: boolean
  /**
   * `filteredChannels` narrowed to the ones that can surface mentions — the
   * scope the Mentions tab works in.
   */
  mentionsChannels: CommentsChannel[]
  isOnlyGoogleBusiness: boolean
  hasNoSupportedChannels: boolean
  /** Whether any viewable channel can surface mentions. */
  hasMentionsSupport: boolean
  canManageChannels: boolean
  isSingleChannel: boolean
  hasAnyViewableChannel: boolean
  channelsFailed: CommentsChannel[]
  channelsNeedingRefreshOnly: CommentsChannel[]
}

type ChannelRefreshNoticesProps = {
  channelsFailed: CommentsChannel[]
  channelsNeedingRefreshOnly: CommentsChannel[]
}

/**
 * Inline refresh banners shown above the feed; renders nothing when there's
 * nothing to flag. Callers gate it with `!mainContent` so banners never show
 * alongside a full-pane override.
 */
function ChannelRefreshNotices({
  channelsFailed,
  channelsNeedingRefreshOnly,
}: ChannelRefreshNoticesProps): JSX.Element | null {
  if (channelsFailed.length === 0 && channelsNeedingRefreshOnly.length === 0) {
    return null
  }
  return (
    <>
      {channelsFailed.length > 0 && (
        <ChannelRefreshUnsuccessfulNotice
          channelsWithFailedRefresh={channelsFailed}
        />
      )}
      {channelsNeedingRefreshOnly.length > 0 && (
        <ChannelRefreshNotice
          channelsNeedingRefresh={channelsNeedingRefreshOnly}
        />
      )}
    </>
  )
}

type GetMainContentArgs = Pick<
  AllChannelsData,
  | 'hasNoSupportedChannels'
  | 'canManageChannels'
  | 'hasAnyViewableChannel'
  | 'isSingleChannel'
  | 'channelsFailed'
  | 'channelsNeedingRefreshOnly'
>

/**
 * Full-pane override for the comments area, or `undefined` to let the normal
 * feed render. Kept as a value (not a component) so callers can use
 * `mainContent ?? <feed>` and pass it to `CommentsByPostView`'s `mainContent`
 * prop — a JSX element is always truthy and would suppress the feed.
 *
 * Priority: no channels → no view access → refresh failed → needs refresh → normal feed.
 */
function getMainContent({
  hasNoSupportedChannels,
  canManageChannels,
  hasAnyViewableChannel,
  isSingleChannel,
  channelsFailed,
  channelsNeedingRefreshOnly,
}: GetMainContentArgs): React.ReactNode {
  // Keep the empty state (illustration + copy), but drop its connect-channel
  // card for actors who can't manage channels — prompting them to connect one
  // they can't connect would be misleading (CORE-6008). Mirrors how Publish
  // hides its connect CTA from non-admins.
  if (hasNoSupportedChannels)
    return <NoSupportedChannelsEmptyState hideConnectCta={!canManageChannels} />
  if (!hasAnyViewableChannel) return <AccessRestricted />
  if (isSingleChannel && channelsFailed.length === 1)
    return <ChannelRefreshUnsuccessful channel={channelsFailed[0]} />
  if (isSingleChannel && channelsNeedingRefreshOnly.length === 1)
    return <ChannelNeedsRefresh channel={channelsNeedingRefreshOnly[0]} />
  return undefined
}

/**
 * Derives every channel-related value the view renders: the comments-supported
 * subset, the filter/disabled sets, the GBP-only flag, and the channel groups
 * that `getMainContent` / `ChannelRefreshNotices` turn into UI.
 *
 * `tab` scopes the channel filter only: which channels it offers depends on the
 * engagement being filtered, since mentions run on a narrower set of channels
 * than comments. Everything else is tab-independent.
 */
function useAllChannelsData({ tab }: { tab: CommunityTab }): AllChannelsData {
  const {
    config: {
      isSupportedInComments,
      supportsMentions,
      channelNeedsRefresh,
      channelRefreshFailed,
    },
  } = useCommunityConfig()
  const { channels: allChannels } = useCommentsChannels()
  // Gates whether the no-supported-channels empty state shows its connect CTA.
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const {
    filters: { selectedChannelIds },
  } = useFilters()

  const channelsSupportedInComments = useMemo(
    () => allChannels.filter(isSupportedInComments),
    [allChannels, isSupportedInComments],
  )

  // Channels the actor can actually `viewComments` on. Everything that reflects
  // what Community actually shows — the comments/score/mentions queries, the
  // refresh banners, and the single-channel treatment — is scoped to this set,
  // not every comments-capable channel. Passing an unviewable channel id to the
  // backend (comments, and notably the multi-channel score use case) makes it
  // throw, surfacing a raw error instead of comments. Mirrors the per-channel
  // gate in `SingleChannelView`.
  const viewableChannels = useMemo(
    () =>
      channelsSupportedInComments.filter((channel) =>
        canPerform(channel, 'viewComments'),
      ),
    [channelsSupportedInComments],
  )

  // Categorise channels by their refresh state in a single pass. Scoped to
  // viewable channels so a banner never points at a channel the actor can't open.
  const { channelsFailed, channelsNeedingRefreshOnly } = useMemo(() => {
    const failed = viewableChannels.filter(channelRefreshFailed)
    const needsRefreshOnly = viewableChannels.filter(
      (c) => channelNeedsRefresh(c) && !channelRefreshFailed(c),
    )
    return {
      channelsFailed: failed,
      channelsNeedingRefreshOnly: needsRefreshOnly,
    }
  }, [viewableChannels, channelNeedsRefresh, channelRefreshFailed])

  // The filter lists every channel, with unsupported ones disabled.
  const channelsForFilter: CommentsChannel[] = allChannels

  // Whether the active tab's engagement works on the channel at all. Mentions
  // run on fewer channels than comments, so the filter must not offer a channel
  // whose selection would only ever yield an empty inbox.
  const supportsActiveTab = useCallback(
    (channel: CommentsChannel): boolean =>
      tab === 'mentions'
        ? supportsMentions(channel)
        : isSupportedInComments(channel),
    [tab, supportsMentions, isSupportedInComments],
  )

  const unsupportedChannelIds = useMemo(
    () =>
      new Set(
        allChannels
          .filter((channel) => !supportsActiveTab(channel))
          .map((channel) => channel.id),
      ),
    [allChannels, supportsActiveTab],
  )

  // Also disabled when the actor lacks `viewComments`, mirroring the
  // per-channel Access Restricted gate (and the `viewableChannels` scoping
  // above) so the filter never offers a channel that would 403 / error.
  const noAccessChannelIds = useMemo(
    () =>
      new Set(
        allChannels
          .filter((channel) => !canPerform(channel, 'viewComments'))
          .map((channel) => channel.id),
      ),
    [allChannels],
  )

  const disabledChannelIds = useMemo(
    () => new Set([...unsupportedChannelIds, ...noAccessChannelIds]),
    [unsupportedChannelIds, noAccessChannelIds],
  )

  // Every disabled row explains itself: greyed-out alone doesn't say whether the
  // channel can't do this at all or is simply out of the actor's reach. Lack of
  // access wins — it's the stronger block, and it holds on either tab.
  const getDisabledChannelReason = useCallback(
    (channelId: string): React.ReactNode => {
      if (noAccessChannelIds.has(channelId)) {
        return NO_COMMUNITY_ACCESS_CHANNEL_TOOLTIP
      }
      if (unsupportedChannelIds.has(channelId)) {
        return tab === 'mentions'
          ? NO_MENTIONS_SUPPORT_CHANNEL_TOOLTIP
          : NO_COMMENTS_SUPPORT_CHANNEL_TOOLTIP
      }
      return undefined
    },
    [noAccessChannelIds, unsupportedChannelIds, tab],
  )

  // Scoped to channels the tab supports so the eye icon never lands on a row
  // that's already greyed out as unsupported.
  const viewOnlyChannelIds = useMemo(
    () =>
      new Set(
        allChannels
          .filter(
            (channel) =>
              supportsActiveTab(channel) && isViewOnlyChannel(channel),
          )
          .map((channel) => channel.id),
      ),
    [allChannels, supportsActiveTab],
  )

  // Channels the actor can manage — the only ones bulk resolve may target. A
  // subset of `viewableChannels` (you can't manage what you can't view), so
  // view-only channels are excluded: the option is hidden and never sent to the
  // dismiss mutations, which reject a mixed batch (mentions) or an all-view-only
  // batch (comments). See CORE-5996.
  const manageableChannelIds = useMemo(
    () =>
      viewableChannels
        .filter((channel) => canPerform(channel, 'manageComments'))
        .map((channel) => channel.id),
    [viewableChannels],
  )

  // The `channels[]` param is shared across the tabs, so a selection made on
  // one can name channels the other can't use. Scoping the feed by the same
  // intersection the filter displays keeps the two in agreement: without it the
  // filter reads "All channels" while the feed stays pinned to a channel the
  // filter has already dropped.
  const effectiveSelectedChannelIds = useMemo(
    () =>
      selectEnabledChannelIds(
        selectedChannelIds ?? [],
        allChannels,
        disabledChannelIds,
      ),
    [selectedChannelIds, allChannels, disabledChannelIds],
  )

  const filteredChannels = useMemo(() => {
    if (effectiveSelectedChannelIds.length === 0) {
      return viewableChannels
    }
    return viewableChannels.filter((channel) =>
      effectiveSelectedChannelIds.includes(channel.id),
    )
  }, [viewableChannels, effectiveSelectedChannelIds])

  // The mentions inbox has no business with channels that can't produce a
  // mention: including them asks the backend for nothing and puts channels in
  // the feed's scope that the filter greys out.
  const mentionsChannels = useMemo(
    () => filteredChannels.filter(supportsMentions),
    [filteredChannels, supportsMentions],
  )

  // When every visible channel is GBP, force list view (GBP shows reviews as a list).
  const isOnlyGoogleBusiness = useMemo(
    () =>
      filteredChannels.length > 0 &&
      filteredChannels.every(isGoogleBusinessChannel),
    [filteredChannels],
  )

  const hasNoSupportedChannels = channelsSupportedInComments.length === 0
  const hasMentionsSupport = viewableChannels.some(supportsMentions)
  // Single-channel treatment keys off the channels the actor can actually open,
  // so one viewable channel alongside hidden ones still gets the focused state.
  const isSingleChannel = viewableChannels.length === 1

  // Whether the actor has `viewComments` on at least one Community-capable
  // channel — mirrors the SingleChannelView `viewComments` gate at the
  // aggregate level (covers every capable channel having `accessLevel: 'none'`).
  const hasAnyViewableChannel = viewableChannels.length > 0

  return {
    channelsSupportedInComments,
    channelsForFilter,
    disabledChannelIds,
    getDisabledChannelReason,
    viewOnlyChannelIds,
    manageableChannelIds,
    viewableChannels,
    filteredChannels,
    isChannelFilterActive: effectiveSelectedChannelIds.length > 0,
    mentionsChannels,
    isOnlyGoogleBusiness,
    hasNoSupportedChannels,
    hasMentionsSupport,
    canManageChannels,
    isSingleChannel,
    hasAnyViewableChannel,
    channelsFailed,
    channelsNeedingRefreshOnly,
  }
}

/**
 * Adds up the counts the community counts context holds for `channels`. A
 * channel the context has no count for adds nothing.
 */
function sumChannelCounts(
  channels: CommentsChannel[],
  getCount: (channelId: string) => number | null,
): number {
  return channels.reduce(
    (total, channel) => total + (getCount(channel.id) ?? 0),
    0,
  )
}

export function AllChannelsView(): JSX.Element {
  const location = useLocation()
  const { postId: selectedPostId } = useParams<{ postId?: string }>()
  const availableTabs = useAvailableTabs()
  const communityCounts = useCommunityCountsContextOptional()
  const totalOrgCommentCount = communityCounts?.totalOrgCount ?? 0
  const totalOrgMentionCount = communityCounts?.totalOrgMentionCount ?? 0
  const [isResolving, setIsResolving] = useState(false)

  const tabParam = new URLSearchParams(location.search).get('tab')
  const tab: CommunityTab =
    isValidCommunityTab(tabParam) && availableTabs.includes(tabParam)
      ? tabParam
      : DEFAULT_TAB

  const {
    channelsSupportedInComments,
    channelsForFilter,
    disabledChannelIds,
    getDisabledChannelReason,
    viewOnlyChannelIds,
    manageableChannelIds,
    viewableChannels,
    filteredChannels,
    isChannelFilterActive,
    mentionsChannels,
    isOnlyGoogleBusiness,
    hasNoSupportedChannels,
    hasMentionsSupport,
    canManageChannels,
    hasAnyViewableChannel,
    isSingleChannel,
    channelsFailed,
    channelsNeedingRefreshOnly,
  } = useAllChannelsData({ tab })

  // The context totals are organisation-wide, so they cannot follow the channel
  // selector. With a selection active, each tab indicator adds up the
  // per-channel counts of the channels in scope. With no selection they read the
  // organisation total, which also covers channels the selector never lists: a
  // locked channel still holds unanswered engagements.
  //
  // Each tab sums over its own scope: `filteredChannels` holds only channels the
  // actor can `viewComments` on, and `mentionsChannels` narrows that to the ones
  // that can surface mentions. The counts query caps each channel at 100, and
  // both indicators render anything above 99 as "99+", so a capped sum shows the
  // badge the true total would show.
  const commentsTabCount = useMemo(() => {
    if (!isChannelFilterActive) return totalOrgCommentCount
    if (!communityCounts) return 0
    return sumChannelCounts(filteredChannels, communityCounts.getChannelCount)
  }, [
    isChannelFilterActive,
    totalOrgCommentCount,
    communityCounts,
    filteredChannels,
  ])

  const mentionsTabCount = useMemo(() => {
    if (!isChannelFilterActive) return totalOrgMentionCount
    if (!communityCounts) return 0
    return sumChannelCounts(
      mentionsChannels,
      communityCounts.getChannelMentionCount,
    )
  }, [
    isChannelFilterActive,
    totalOrgMentionCount,
    communityCounts,
    mentionsChannels,
  ])

  // Backfill banner sits at the panel level (above channel notices and the
  // list/by-post content); show it for whichever channel is currently syncing.
  const backfillChannel = useActiveBackfillChannel(filteredChannels)

  // Derived over every viewable channel, not the filtered scope: while a
  // healthy mention channel exists anywhere, the filters must stay so the
  // actor can reach it — even if the current selection shows the refresh
  // empty state.
  const { allChannelsNeedRefresh: allMentionChannelsNeedRefresh } =
    useMentionsNeedsRefresh(viewableChannels)

  const mainContent = getMainContent({
    hasNoSupportedChannels,
    canManageChannels,
    hasAnyViewableChannel,
    isSingleChannel,
    channelsFailed,
    channelsNeedingRefreshOnly,
  })

  const { shouldShowCoachMark, triggerCoachMark, dismissCoachMark } =
    useBannerToCoachMarkFlow()

  const {
    actions: { setStatus, setSortOption, setPublishedAt },
  } = useFilters()

  const { view, setViewPreference } = usePersistedViewPreference({
    isGoogleBusiness: isOnlyGoogleBusiness,
  })

  const { handleTabsChange } = useTabNavigation({ availableTabs })

  const { handleStatusChange, handleDateRangeChange, handleSortOptionChange } =
    useFilterHandlers({
      channels: channelsSupportedInComments,
      filterActions: { setStatus, setSortOption, setPublishedAt },
    })

  const { trackCommentsFiltered } = useCommentsTracking()

  // Scopes bulk actions to the selected post in the by-post view.
  const postIdForView = view === 'posts' ? selectedPostId : undefined
  const showTabBar = availableTabs.length > 1
  const shouldShowViewToggle = tab === 'comments'
  // Views that fill the viewport and manage their own scrolling, rather than
  // scrolling as one page: the by-post split panel and the Messages panes.
  const isSplitView = (tab === 'comments' && view === 'posts') || tab === 'dms'

  const handleToggleGroupChange = (value: string): void => {
    if (value === 'posts' || value === 'comments') {
      setViewPreference(value)
      trackCommentsFiltered({
        channels: filteredChannels.map((channel) => ({
          id: channel.id,
          service: channel.service,
        })),
        filterType: 'viewMode',
        filterValue: value,
      })
    }
  }

  return (
    <>
      <PersistedStatusSync />
      <Outlet />
      <PageLayoutMotion
        layoutScroll
        className={clsx(isSplitView && styles.layout)}
      >
        <Tabs
          defaultValue={DEFAULT_TAB}
          value={tab}
          onChange={handleTabsChange}
          className={clsx(styles.tabs, isSplitView && styles.splitView)}
        >
          <PageHeader className={styles.pageHeader}>
            <PageHeader.Row>
              <CommunityPageTitle channels={viewableChannels} />
              <PageHeader.Actions className={styles.headerActions}>
                <PageHeader.ActionGroup>
                  {/* Every viewable channel, not the filtered set: a channel
                      filter must not make mixed access look all-view-only. */}
                  <ViewOnlyHintButton channels={viewableChannels} />
                  <FeedbackWidget
                    id="all-channels-view"
                    source="comment"
                    className={pageHeaderStyles.hideOnNarrow}
                  />
                  {/* Hidden on narrow to match the single-channel header,
                      where the sync control is the first thing to go. */}
                  <Flex
                    align="center"
                    className={pageHeaderStyles.hideOnNarrow}
                  >
                    <AllChannelsSyncActions channels={viewableChannels} />
                  </Flex>
                </PageHeader.ActionGroup>
                {shouldShowViewToggle && !isOnlyGoogleBusiness && (
                  <NarrowHeaderSlot narrow={false}>
                    <PageHeader.ActionGroup>
                      <CommentsOrPostsViewToggle
                        value={view}
                        handleToggleGroupChange={handleToggleGroupChange}
                        className={styles.toggleViewAction}
                      />
                    </PageHeader.ActionGroup>
                  </NarrowHeaderSlot>
                )}
              </PageHeader.Actions>
              {filteredChannels.length > 0 && tab === 'mentions' && (
                <NewMentionsAvailableButton />
              )}
              {filteredChannels.length > 0 && tab === 'comments' && (
                <NewCommentsAvailableButton
                  channelIds={filteredChannels.map((c) => c.id)}
                />
              )}
            </PageHeader.Row>
            {/* Secondary row: tab list (when there's more than one tab) + filters. */}
            <PageHeader.Row className={styles.toolbarRow}>
              {showTabBar && (
                <CommunityTabBar
                  tabs={availableTabs}
                  value={tab}
                  onChange={handleTabsChange}
                  commentsCount={commentsTabCount}
                  mentionsCount={mentionsTabCount}
                  mentionsSupported={hasMentionsSupport}
                />
              )}
              {/* The filter/sort/resolve toolbar acts on the feed. The
                  mentions tab swaps the feed for the not-supported state (no
                  mention-capable channel) or the refresh empty state (every
                  mention channel blocked), and then the toolbar has nothing
                  to act on. Messages scopes its own controls to the
                  conversation list, so it takes none from the page header. */}
              {(tab === 'comments' ||
                (tab === 'mentions' &&
                  hasMentionsSupport &&
                  !allMentionChannelsNeedRefresh)) && (
                <PageHeader.Actions className={styles.headerActions}>
                  <CommunityFilters
                    setStatus={handleStatusChange}
                    setSortOption={handleSortOptionChange}
                    setPublishedAt={handleDateRangeChange}
                    channelIds={manageableChannelIds}
                    postId={postIdForView}
                    onResolvingChange={setIsResolving}
                    showBannerDismissedCoachMark={shouldShowCoachMark}
                    onBannerDismissedCoachMarkDismiss={dismissCoachMark}
                    channels={
                      // useCommentsChannels returns unmasked channel objects; CommunityFilters expects FilterByChannel's masked type. The fields are present, so bridge the missing fragment brand at this boundary.
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                      channelsForFilter as unknown as MaskedFilterByChannelFragment
                    }
                    disabledChannelIds={disabledChannelIds}
                    getDisabledChannelReason={getDisabledChannelReason}
                    viewOnlyChannelIds={viewOnlyChannelIds}
                    entity={tab === 'mentions' ? 'mentions' : 'comments'}
                  />
                  {shouldShowViewToggle && !isOnlyGoogleBusiness && (
                    <NarrowHeaderSlot narrow>
                      <CommentsOrPostsViewToggle
                        value={view}
                        handleToggleGroupChange={handleToggleGroupChange}
                      />
                    </NarrowHeaderSlot>
                  )}
                </PageHeader.Actions>
              )}
            </PageHeader.Row>
          </PageHeader>
          <Tabs.Panel
            value="comments"
            aria-label={TAB_LABELS.comments}
            className={styles.panel}
          >
            {backfillChannel && (
              <BackfillLoadingBanner channel={backfillChannel} fullBleed />
            )}
            {view === 'posts' ? (
              // Full-width container so the posts view can host the sidepanel.
              <PageLayout.Container
                size="full"
                className={styles.postsContainer}
              >
                <CommentsByPostView
                  channels={filteredChannels}
                  isAllChannelsView
                  noChannelsConnected={hasNoSupportedChannels}
                  mainContent={mainContent}
                  notice={
                    mainContent ? undefined : (
                      <ChannelRefreshNotices
                        channelsFailed={channelsFailed}
                        channelsNeedingRefreshOnly={channelsNeedingRefreshOnly}
                      />
                    )
                  }
                />
              </PageLayout.Container>
            ) : (
              <PageLayout.Container
                size="narrow"
                className={styles.commentsContainer}
              >
                {mainContent ?? (
                  <>
                    <ChannelRefreshNotices
                      channelsFailed={channelsFailed}
                      channelsNeedingRefreshOnly={channelsNeedingRefreshOnly}
                    />
                    <CommentsList
                      channels={filteredChannels}
                      isLoading={isResolving}
                      onBannerDismissed={triggerCoachMark}
                    />
                  </>
                )}
              </PageLayout.Container>
            )}
          </Tabs.Panel>
          <Tabs.Panel
            value="mentions"
            aria-label={TAB_LABELS.mentions}
            className={styles.panel}
          >
            <PageLayout.Container size="narrow">
              <MentionsCounterPromoBanner count={totalOrgMentionCount} />
              {/* The notice speaks for the channels the feed leaves out, so it
                  takes the unnarrowed set the feed is derived from. */}
              <MentionsInstagramLoginNotice channels={filteredChannels} />
              <MentionsList channels={mentionsChannels} />
            </PageLayout.Container>
          </Tabs.Panel>
          <Tabs.Panel
            value="dms"
            aria-label={TAB_LABELS.dms}
            className={styles.panel}
          >
            {/* Full-width: the Messages view hosts its own panes. */}
            <PageLayout.Container size="full" className={styles.dmsContainer}>
              <DMsView channels={filteredChannels} />
            </PageLayout.Container>
          </Tabs.Panel>
        </Tabs>
      </PageLayoutMotion>
    </>
  )
}
